import Territory from './political/territory';
import Calendar from 'model/calendar';

class Market {
  // Generosity
  static Generosity = {
    Generous: 0,
    Fair: 10,
    Greedy: 20
  };

  static BASE_PRICES = {
    horse: 50,
    medicine: 5,
    food: 2,
    water: 1,
    ammo: 5
  };

  static BASE_QUANTITIES = {
    horse: 1,
    medicine: 50,
    food: 200,
    water: 500,
    ammo: 100,
    coins: 0
  };

  static QUANTITY_MULTIPLIERS = {
    relationship: {
      [Territory.Attitude.Hostile]: 0.5,
      [Territory.Attitude.Suspicious]: 0.9,
      [Territory.Attitude.Neutral]: 1,
      [Territory.Attitude.Friendly]: 1.5
    },
    season: {
      [Calendar.Winter]: 0.85,
      [Calendar.Spring]: 1,
      [Calendar.Summer]: 1,
      [Calendar.Autum]: 1.2
    }
  };

  static PRICE_MULTIPLIERS = {
    relationship: {
      [Territory.Attitude.Hostile]: 1.5,
      [Territory.Attitude.Suspicious]: 1.2,
      [Territory.Attitude.Neutral]: 1,
      [Territory.Attitude.Friendly]: 0.9
    },
    generosity: {
      [Market.Generosity.Greedy]: 1.1,
      [Market.Generosity.Fair]: 1,
      [Market.Generosity.Generous]: 0.9
    },
    season: {
      [Calendar.Winter]: 1.2,
      [Calendar.Spring]: 1,
      [Calendar.Summer]: 1,
      [Calendar.Autum]: 0.85
    }
  };

  constructor({ season, generosity, friendliness, supplyDemandFactor = 1 }) {
    this.init({ season, generosity, friendliness, supplyDemandFactor });
    this.adjustment = this.initAdjustments();
  }

  init({ season, generosity, friendliness, supplyDemandFactor }) {
    this.season = season;
    this.generosity = generosity;
    this.friendliness = friendliness;
    this.supplyDemandFactor = supplyDemandFactor;

    this.prices = this.calculatePrices();
    this.quantities = this.calculateQuantities();
  }

  initAdjustments() {
    let adjustments = {};
    Object.keys(Market.BASE_QUANTITIES).forEach(item => {
      adjustments[item] = 0;
    });
    return adjustments;
  }

  update({ season, generosity, friendliness, supplyDemandFactor }) {
    this.init({ season, generosity, friendliness, supplyDemandFactor });
    this.quantities = this.calculateQuantities();
  }

  calculateQuantities() {
    const relationshipModifier = Market.QUANTITY_MULTIPLIERS.relationship[this.friendliness];
    const seasonModifier = Market.QUANTITY_MULTIPLIERS.season[this.season];

    let calculatedQuantities = {};
    Object.keys(Market.BASE_QUANTITIES).forEach(item => {
      // Modify the quantity based on relationship modifier and season factor
      const adjustedQuantity = Math.ceil(Market.BASE_QUANTITIES[item] * relationshipModifier * seasonModifier);

      // Adjust the quantity based on purchases
      const adjustment = this.adjustment?.[item] || 0;
      calculatedQuantities[item] = adjustedQuantity + adjustment;
    });
    return calculatedQuantities;
  }

  // TODO: Adjust the price or quantity based on environmental factors
  adjustForEnvironmentalFactors = (context, basePrice, baseQuantity) => {
    // Example: Increase price during drought
    if (context.environmentalState === 'drought') {
      basePrice *= 1.5; // Increase price by 50%
    }
    // Or, offer more water for the same price during rainy seasons
    if (context.environmentalState === 'rainy') {
      baseQuantity *= 1.2; // 20% more water
    }
    return { adjustedPrice: basePrice, adjustedQuantity: baseQuantity };
  };

  calculatePrices() {
    const seasonModifier = Market.PRICE_MULTIPLIERS.season[this.season];
    const generosityModifier = Market.PRICE_MULTIPLIERS.generosity[this.generosity];
    const relationshipModifier = Market.PRICE_MULTIPLIERS.relationship[this.friendliness];
    const dynamicModifier = this.supplyDemandFactor;

    let calculatedPrices = {};
    Object.keys(Market.BASE_PRICES).forEach(item => {
      const basePrice = Market.BASE_PRICES[item];
      const priceBeforeRounding =
        basePrice * seasonModifier * generosityModifier * relationshipModifier * dynamicModifier;

      calculatedPrices[item] = priceBeforeRounding;
      // round to the nearest decimal
      calculatedPrices[item] = Math.round(priceBeforeRounding * 10) / 10;
    });

    return calculatedPrices;
  }

  updateSeason(newSeason) {
    this.season = newSeason;
    this.prices = this.calculatePrices();
    this.quantities = this.calculateQuantities();
  }

  updateGenerosity(newGenerosity) {
    this.generosity = newGenerosity;
    this.prices = this.calculatePrices();
    this.quantities = this.calculateQuantities();
  }

  updateFriendliness(newFriendliness) {
    this.friendliness = newFriendliness;
    this.prices = this.calculatePrices();
    this.quantities = this.calculateQuantities();
  }

  updateSupplyDemandFactor(newFactor) {
    this.supplyDemandFactor = newFactor;
    this.prices = this.calculatePrices(); // Recalculate prices with the new factor
    this.quantities = this.calculateQuantities();
  }

  // Example method to dynamically adjust the market based on game events
  handleGameEvent(eventType, eventData) {
    // Adjust the supplyDemandFactor based on specific events
    // This is a placeholder for how you might implement dynamic adjustments
    if (eventType === 'harvest') {
      this.updateSupplyDemandFactor(eventData.harvestQuality === 'poor' ? 1.2 : 0.8);
    } else if (eventType === 'tradeBlockade') {
      this.updateSupplyDemandFactor(1.5);
    } else {
      this.updateSupplyDemandFactor(1); // Reset to default if the event doesn't affect supply/demand
    }
  }

  getPrices() {
    return this.prices;
  }

  getQuantities() {
    return this.quantities;
  }

  delta(purchase) {
    //{ food, water, ammo, medicine, horse, coins })
    Object.keys(purchase).forEach(key => {
      this.adjustment[key] -= purchase[key];
    });
    this.quantities = this.calculateQuantities();
  }

  getMarketState() {
    // Return both prices and quantities
    return {
      prices: this.prices,
      quantities: this.quantities
    };
  }
}

export default Market;
